/**
 * VoiceProviderTabs Component
 * Tabs to switch between voice providers (OpenAI, Gemini, ElevenLabs, etc.)
 */

import React from 'react';
import { motion } from 'framer-motion';
import { FiMic } from 'react-icons/fi';
import type { VoiceProvider } from '../../types/voice.types';

interface VoiceProviderTabsProps {
  activeProvider: VoiceProvider;
  onChange: (provider: VoiceProvider) => void;
  className?: string;
}

const PROVIDERS: Array<{ id: VoiceProvider; label: string; color: string }> = [
  { id: 'openai', label: 'OpenAI', color: 'text-green-400' },
  { id: 'gemini', label: 'Gemini', color: 'text-blue-400' },
  { id: 'elevenlabs', label: 'ElevenLabs', color: 'text-purple-400' },
  { id: 'azure', label: 'Azure', color: 'text-cyan-400' },
  { id: 'google', label: 'Google', color: 'text-yellow-400' }
];

export const VoiceProviderTabs: React.FC<VoiceProviderTabsProps> = ({
  activeProvider,
  onChange,
  className = ''
}) => {
  return (
    <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <FiMic className="text-blue-400" size={20} />
        <h3 className="text-lg font-semibold text-white">Voice Provider</h3>
      </div>

      <div className="flex gap-2 border-b border-slate-700">
        {PROVIDERS.map((provider) => {
          const isActive = activeProvider === provider.id;

          return (
            <button
              key={provider.id}
              onClick={() => onChange(provider.id)}
              className={`
                relative px-4 py-3 flex items-center gap-2 transition-colors
                ${isActive
                  ? 'text-white'
                  : 'text-gray-400 hover:text-gray-300'
                }
              `}
            >
              <span className={isActive ? provider.color : ''}>
                {provider.label}
              </span>

              {isActive && (
                <motion.div
                  layoutId="activeProvider"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </button>
          );
        })}
      </div>

      <p className="text-sm text-gray-400 mt-3">
        Each provider offers different voice options, languages, and pricing
      </p>
    </div>
  );
};

export default VoiceProviderTabs;
